import type { QuoteLine, QuoteTotals } from '../api/types'
import { numberValue } from '../ui/numbers'

/** The parts of a stored version snapshot (the quote read model at that revision) the history uses. */
export type VersionSnapshot = {
  title?: string
  lines?: QuoteLine[]
  totals?: QuoteTotals
  freight_amount?: string
  tax_rate?: string
  deliver_to?: Record<string, unknown>
  notes?: string | null
}

export type LineChange = {
  kind: 'added' | 'removed' | 'changed'
  line: QuoteLine
  fields: Array<{ label: string; before: string | number | null; after: string | number | null; money?: boolean }>
}

export type VersionComparison = {
  lines: LineChange[]
  totals: Array<{ label: string; before: string; after: string }>
}

export function snapshotOf(value: unknown): VersionSnapshot {
  return value && typeof value === 'object' ? (value as VersionSnapshot) : {}
}

const sell = (line: QuoteLine) => line.sell_amount ?? line.list_amount

/** What changed between two versions: lines added, removed or edited, and the totals that moved. */
export function compareVersions(older: VersionSnapshot, newer: VersionSnapshot): VersionComparison {
  const before = new Map((older.lines ?? []).map((line) => [line.id, line]))
  const after = new Map((newer.lines ?? []).map((line) => [line.id, line]))
  const lines: LineChange[] = []
  for (const line of newer.lines ?? []) {
    const previous = before.get(line.id)
    if (!previous) {
      lines.push({ kind: 'added', line, fields: [] })
      continue
    }
    const fields: LineChange['fields'] = []
    if (numberValue(previous.quantity) !== numberValue(line.quantity)) {
      fields.push({ label: 'Qty', before: numberValue(previous.quantity), after: numberValue(line.quantity) })
    }
    if (numberValue(sell(previous)) !== numberValue(sell(line))) {
      fields.push({ label: 'Sell', before: sell(previous), after: sell(line), money: true })
    }
    if (previous.description !== line.description) {
      fields.push({ label: 'Description', before: previous.description, after: line.description })
    }
    if (JSON.stringify(previous.configuration_display ?? {}) !== JSON.stringify(line.configuration_display ?? {})) {
      fields.push({
        label: 'Configuration',
        before: Object.values(previous.configuration_display ?? {}).join(', ') || null,
        after: Object.values(line.configuration_display ?? {}).join(', ') || null,
      })
    }
    if (fields.length) lines.push({ kind: 'changed', line, fields })
  }
  for (const line of older.lines ?? []) {
    if (!after.has(line.id)) lines.push({ kind: 'removed', line, fields: [] })
  }
  const totals: VersionComparison['totals'] = []
  for (const [key, label] of [
    ['sell', 'Sell'],
    ['freight', 'Freight'],
    ['tax', 'Tax'],
    ['grand_total', 'Total'],
  ] as const) {
    const was = older.totals?.[key]
    const now = newer.totals?.[key]
    if (was !== undefined && now !== undefined && numberValue(was) !== numberValue(now)) {
      totals.push({ label, before: String(was), after: String(now) })
    }
  }
  return { lines, totals }
}
